import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CreateContractOffer, getCommercialsBid } from '../../services/procurementService';
import { TenderDocumentType } from '../../types/enums';
import ConfirmationModal from '../../components/ConfirmModal/ConfirmationModal';
import { useEffect, useState } from 'react';
import { getProcuremtDocuments, viewDocuments } from '../../services/edmsService';
import { getUploadedDocName } from '../../utils/helper';
import { useTranslation } from 'react-i18next';
import { BidApplicantsResponse, contractOfferPayload } from '../../types/procurement-types';
import moment from 'moment';
import { XIcon } from 'lucide-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import FormikTextarea from '../../components/CustomTextArea/CustomTextArea';
import FormikInput from '../../components/CustomTextInput/CustomTextInput';

interface PaymentTerm {
  description: string;
  amount: number;
}

// Add this validation schema
const PaymentTermsSchema = Yup.object().shape({
  paymentTerms: Yup.array().of(
    Yup.object().shape({
      description: Yup.string().required('Description is required'),
      amount: Yup.number().required('Amount is required').min(0, 'Amount must be positive'),
    })
  ),
});

const CommercialInformationProposalPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { tenderBidId, serviceInfoId } = useParams<{
    tenderBidId: string;
    serviceInfoId: string;
  }>();

  const [modalOpen, setModalOpen] = useState(false);
  const [bidInfo, setBidInfo] = useState<BidApplicantsResponse>();
  const [actionStatus, setActionStatus] = useState<'Approved' | 'Denied' | null>(null);

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const { data: CommercialsBidResponse } = useQuery({
    queryKey: ['bids-commercials', tenderBidId],
    queryFn: async () => {
      try {
        const response = await getCommercialsBid(tenderBidId as string);
        if (response.isSuccess) return response?.data;
      } catch (error) {
        toast.error(
          t(
            'failedToFetchCommercials',
            'Failed to fetch commercial information. Please try again later.'
          )
        );
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const { data: commercialBidDocument } = useQuery({
    queryKey: ['bids-expression-document', tenderBidId],
    queryFn: async () => {
      try {
        const response = await getProcuremtDocuments({
          entityId: tenderBidId,
          subModule: TenderDocumentType.COMMERCIALS,
        });
        if (response.isSuccess) return response?.data || [];
      } catch (error) {
        toast.error(
          t('failedToFetchDocuments', 'Failed to fetch documents. Please try again later.')
        );
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const approveOrRejectMutation = useMutation({
    mutationFn: (payload: contractOfferPayload) => CreateContractOffer(payload),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success(response.data.message);
        setTimeout(() => {
          navigate(`/procurement/proposals/${serviceInfoId}?tab=allBids`, { replace: true });
        }, 2000);
      } else {
        toast.error(response.errorMessage);
      }
    },
    onError: () => {
      toast.error(t('actionFailed', 'Unable to perform action, please try again later'));
    },
  });

  useEffect(() => {
    if (location.state && location.state.bidItem) {
      const { bidItem } = location.state;
      setBidInfo(bidItem);
    }
  }, [location.state]);

  const handleAction = (status: 'Approved' | 'Denied') => {
    setActionStatus(status);
    if (status === 'Approved') {
      setIsAddModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const confirmAction = () => {
    if (actionStatus) {
      approveOrRejectMutation.mutate({
        tenderType: TenderDocumentType.COMMERCIALS,
        status: actionStatus,
        tenderBidId: tenderBidId as string,
        paymentTerms: paymentTerms,
      });
      setModalOpen(false);
      setPaymentTerms([]);
    }
  };

  const handleFetchDocuments = async (selectedDocumentId: string, docName: string) => {
    try {
      const response = await viewDocuments({
        documentId: selectedDocumentId,
        module: 'procurement',
      });
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${docName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('An error occurred:', error);
      toast.error(t('errorOccurred', 'An error occurred. Please try again.'));
    }
  };

  const actionText = actionStatus === 'Approved' ? t('approve', 'approve') : t('deny', 'deny');

  const message = t('confirmActionMessage', 'Are you sure you want to {action} this bid?').replace(
    '{action}',
    actionText
  );

  return (
    <div className="mx-auto max-w-screen-xl p-8">
      <h1 className="mb-1 text-2xl font-semibold text-gray-900">
        {t('commercialInformation', 'Commercial Information/Financial Projection')}
      </h1>

      <div className="2xl:w-11/12">
        <div className="mb-2.5 mt-8 flex flex-col flex-wrap justify-between gap-x-7 gap-y-4 rounded-xl border border-[#E5E5E5] bg-white p-5 pb-12 xl:flex-row xl:items-center">
          <div className="flex w-full flex-wrap justify-between gap-3">
            <div className="flex items-center gap-x-6">
              <img
                src="https://placehold.co/400"
                alt="vendor"
                className="h-[58px] w-[58px] rounded-full"
              />
              <div className="flex flex-col items-start gap-y-2">
                <p className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#101828] underline">
                  {bidInfo?.applicantName}
                </p>
                <span className="rounded-[30px] bg-[#42BE65] px-2.5 py-1 text-[10px] font-medium text-white">
                  {t('verifiedSupplier', 'Verified Supplier')}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">
                {t('tenderOpportunity', 'Tender Opportunity')}
              </span>
              <span className="text-sm font-medium text-[#525252]">
                Pipeline Inspection and Maintenance
              </span>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">{t('type', 'Type')}</span>
              <span className="text-sm font-medium text-[#525252]">{bidInfo?.businessType}</span>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="text-sm text-[#8D8D8D]">{t('submitted', 'Submitted')}</span>
              <span className="text-sm font-medium text-[#525252]">
                {moment(bidInfo?.submissionDate).format('DD/MM/YYYY')}
              </span>
            </div>
          </div>

          {/* <button className="ml-auto flex h-12 w-[140px] items-center justify-center rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm">
            {t('viewDetails', 'View Details')}
          </button> */}
        </div>

        <div className="my-4 rounded-lg bg-[#24A148] px-5">
          <p className="text-xl font-semibold text-white">{t('stage3Of3', 'Stage 3 of 3')}</p>
        </div>

        {CommercialsBidResponse ? (
          <>
            <div className="mb-4 rounded-2xl border border-[#E5E5E5] bg-white pb-[54px] pl-[34px] pr-[26px] pt-[33px]">
              <h3 className="mb-[34px] text-xl font-medium text-[#101828]">
                {t('commercialBreakdown', 'Commercial Information Breakdown')}
              </h3>

              <div className="mx-auto grid w-3/4 grid-cols-2 gap-x-[10%] gap-y-4">
                <div className="flex flex-col gap-y-4">
                  <span className="text-sm font-medium text-[#8D8D8D]">
                    {t('costBreakdown', 'Cost Breakdown')}
                  </span>
                  <span className="pb-2.5 text-[32px] font-semibold text-[#24A148]">
                    $
                    {CommercialsBidResponse?.cost
                      ? new Intl.NumberFormat('en-US').format(CommercialsBidResponse?.cost)
                      : ''}
                  </span>
                </div>

                <div className="flex flex-col gap-y-4">
                  <span className="text-sm font-medium text-[#8D8D8D]">
                    {t('finalTotalCost', 'Final Total Cost (USD)')}
                  </span>
                  <span className="pb-2.5 text-[32px] font-semibold text-[#24A148]">
                    $
                    {CommercialsBidResponse?.quotedCost
                      ? new Intl.NumberFormat('en-US').format(CommercialsBidResponse?.quotedCost)
                      : ''}
                  </span>
                </div>

                <div className="flex flex-col gap-y-4">
                  <span className="text-sm font-medium text-[#8D8D8D]">
                    {t('totalQuotedCost', 'Total Quoted Cost (USD)')}
                  </span>
                  <span className="pb-2.5 text-[32px] font-semibold text-[#24A148]">
                    $
                    {CommercialsBidResponse?.totalCost
                      ? new Intl.NumberFormat('en-US').format(CommercialsBidResponse?.totalCost)
                      : ''}
                  </span>
                </div>

                <div className="flex flex-col gap-y-4">
                  <span className="text-sm font-medium text-[#8D8D8D]">
                    {t('discountOffered', 'Discount Offered (USD)')}
                  </span>
                  <span className="pb-2.5 text-[32px] font-semibold text-[#24A148]">
                    $
                    {CommercialsBidResponse?.discount
                      ? new Intl.NumberFormat('en-US').format(CommercialsBidResponse?.discount)
                      : ''}
                  </span>
                </div>
              </div>

              <div className="mt-10">
                <h3 className="mb-[34px] text-xl font-medium text-[#101828]">
                  {t('requiredDocuments', 'Required Documents')}
                </h3>

                {commercialBidDocument?.map((doc: any) => {
                  const docName = getUploadedDocName(doc.documentName);
                  return (
                    <div key={doc.tenderBidDocumentId} className="mx-auto w-3/4 gap-y-4">
                      <div className="flex flex-col gap-y-4">
                        <span className="text-sm font-medium text-[#8D8D8D]">
                          {doc?.documentTypeName}
                        </span>
                        <div
                          className="flex items-center gap-x-[30px] rounded-[5px] bg-[#F5F5F5] py-2.5 text-sm text-[#667085]"
                          role="button"
                          onClick={() => handleFetchDocuments(doc.tenderBidDocumentId, docName)}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/images/dashicons_pdf.svg`}
                            alt="pdf icon"
                          />
                          {docName}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-[32px]">
              <button
                className="rounded-lg border border-[#FA4D56] px-5 py-3 text-sm font-semibold text-[#FA4D56]"
                onClick={() => handleAction('Denied')}
                disabled={approveOrRejectMutation.isPending}
              >
                {t('reject', 'Reject')}
              </button>

              <button
                className="rounded-lg border border-[#24A148] bg-[#24A148] px-5 py-3 text-sm font-semibold text-white"
                onClick={() => handleAction('Approved')}
                disabled={approveOrRejectMutation.isPending}
              >
                {t('approve', 'Approve')}
              </button>
            </div>
          </>
        ) : (
          <div className="flex min-h-[40vh] items-center justify-center border border-[#E5E5E5] bg-white">
            <div>
              <h3 className="text-center text-2xl font-semibold text-[#161616]">
                {t('noRecordFound', 'No record found')}
              </h3>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmAction}
        title={t('confirmAction', 'Confirm Action')}
        message={message}
      />

      {(isAddModalOpen || modalOpen) && (
        <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="max-h-[65vh] w-2/3 overflow-auto rounded-[30px] bg-white px-[65px] py-[55px] xl:max-h-none">
            <div className="mb-8 flex items-center justify-between">
              <h3 className="text-base font-medium text-black">
                {t('addPaymentTerms', 'Add Payment Terms')}
              </h3>

              <button
                className="rounded-full bg-[#F5F5F5] p-1"
                onClick={() => {
                  if (isAddModalOpen) setIsAddModalOpen(false);
                }}
              >
                <XIcon className="h-4 w-4 text-[#1C1B1F]" />
              </button>
            </div>

            {paymentTerms.length > 0 && (
              <>
                <div className="w-3/5">
                  <div className="mb-4 grid grid-cols-3 border-b border-[#E5E5E5] pb-4 text-sm font-medium text-[#525252]">
                    <span className="">Milestone</span>
                    <span className="">Amount</span>
                  </div>

                  {paymentTerms.map((paymentTerm, index) => (
                    <div
                      key={index}
                      className="mb-4 grid grid-cols-3 border-b border-[#E5E5E5] pb-4 text-sm font-medium text-[#101828]"
                    >
                      <span className="">{paymentTerm?.description}</span>

                      <span className="">
                        ${new Intl.NumberFormat('en-US').format(paymentTerm?.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {isAddModalOpen ? (
              <Formik
                initialValues={{
                  description: '',
                  amount: 0,
                }}
                validationSchema={PaymentTermsSchema}
                onSubmit={(values, { resetForm }) => {
                  setPaymentTerms([...paymentTerms, values]);
                  resetForm();
                  //   setIsAddModalOpen(false);
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <button
                      className="ml-auto mt-7 flex h-9 items-center justify-center rounded-lg border border-[#101828] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {t('add', '+ Add')}
                    </button>

                    <div className="flex w-full gap-x-4">
                      <FormikInput
                        label={t('description')}
                        name="description"
                        placeholder={t('describeTheItem')}
                        isRequired
                        // disabled
                      />

                      <FormikInput
                        label={t('amount')}
                        name="amount"
                        placeholder={t('enterAmount')}
                        inputClassName="max-w-[30%]"
                        isRequired
                        type="text"
                      />
                    </div>

                    <div className="mt-10 flex justify-end">
                      <button
                        type="button"
                        className="rounded-lg bg-[#24A148] px-4 py-2 text-sm font-medium text-white"
                        onClick={() => {
                          setActionStatus('Approved');
                          confirmAction();
                        }}
                        disabled={approveOrRejectMutation.isPending}
                      >
                        {t('sendContractOffer', 'Send contract offer')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <>
                <div className="mb-6">
                  <h4 className="mb-2 text-sm font-medium">Payment Terms</h4>
                  {paymentTerms.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {paymentTerms.map((term, index) => (
                        <li key={index} className="py-2">
                          <div className="flex justify-between">
                            <span>{term.description}</span>
                            <span>${term.amount.toLocaleString()}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No payment terms added</p>
                  )}
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    className="rounded-lg border border-[#D0D5DD] px-4 py-2 text-sm font-medium"
                    onClick={() => {
                      setIsAddModalOpen(true);
                      setModalOpen(false);
                    }}
                  >
                    Add Payment Terms
                  </button>

                  <button
                    type="button"
                    className="rounded-lg bg-[#24A148] px-4 py-2 text-sm font-medium text-white"
                    onClick={() => {
                      confirmAction();
                      setModalOpen(false);
                    }}
                    disabled={approveOrRejectMutation.isPending}
                  >
                    Confirm {actionStatus === 'Approved' ? 'Approval' : 'Rejection'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommercialInformationProposalPage;
